import { model as router } from '!/router'
import { model as redirectUrlModel } from '$/redirect-url'
import { model as config, remote } from '@/config'
import { F, T } from '@/helpers'
import { PriceSettingsType, api, type TvShowEpisode } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
  split,
} from 'effector'
import { model as catchupsModel } from '~/entities/catchups'
import {
  MediaTypeInternal,
  MediaTypes,
  MediaTypesExtended,
  type ExternalPaymentParams,
  type MediaTypesEx,
} from '../index.h'
import { $isAnyPaymentPopupOpened } from './model'

export type ISelectedPaymentContent = {
  mediaType?: MediaTypesEx | null
  externalId?: string | null
  priceType?: PriceSettingsType | null
} | null

export const selectForExternalPayment = createEvent<ExternalPaymentParams>()

export const $selectedContent = createStore<ISelectedPaymentContent>(null).on(
  selectForExternalPayment,
  (_, selectedItem) => selectedItem
)

export const redirectToPlayerOrRefresh =
  createEvent<Pick<ExternalPaymentParams, 'mediaType' | 'params'>>()

export const $externalPaymentBaseUrl = config.get(remote.tria_shoppingCartUrl)

export const $externalPaymentEnabled = $externalPaymentBaseUrl.map(Boolean)

export const openExternalPaymentPopup = createEvent()
export const closeExternalPaymentPopup = createEvent()

export const $externalPaymentPopupIsOpened = createStore(false).reset(
  router.$pathname
)

// open popup
sample({
  clock: openExternalPaymentPopup,
  filter: $externalPaymentEnabled,
  fn: T,
  target: $externalPaymentPopupIsOpened,
})

// close popup
sample({
  clock: closeExternalPaymentPopup,
  fn: F,
  target: $externalPaymentPopupIsOpened,
})

sample({
  clock: $externalPaymentPopupIsOpened,
  target: $isAnyPaymentPopupOpened,
})

const isTvChannelPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const channels = await api.tv.getTvChannelsByIdFx({ ids: [id] })
  return channels?.[0]?.purchaseInfo != null
})

const isTvChannelBundlePurchasedFx = createEffect(
  async ({ id }: { id: number }) => {
    const bundle = await api.tvBundles.getTvBundleInfoFx({ bundleId: id })
    return bundle?.purchaseInfo != null
  }
)

const isVodPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const vod = await api.vods.getVodFx({ vodId: id })
  return vod?.purchaseInfo != null
})

const isPartnerProductPurchasedFx = createEffect(
  async ({ id }: { id: number }) => {
    const partnerProduct = await api.partnerProgram.getPartnerProductDetailsFx({
      partnerProductId: id,
    })
    return partnerProduct?.purchaseInfo != null
  }
)

const isLiveEventPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const liveEvent = await api.liveEvents.getLiveEventByIdFx({
    eventId: id,
  })
  return liveEvent?.purchaseInfo != null
})

const isVodBundlePurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const bundle = await api.vodBundles.getVodBundleInfoFx({ bundleId: id })
  return bundle?.purchaseInfo != null
})

const isTvShowPurchasedFx = createEffect(async ({ id }: { id: number }) => {
  const shows = await api.tvshows.getTvShowBaseFx({ tvShowId: id })

  return shows?.[0]?.purchaseInfo != null
})

const isTvShowSeasonPurchasedFx = createEffect(
  async ({ id, tvShowId }: { id: number; tvShowId: number }) => {
    const season = await api.tvshows.getTvShowSeasonByIdFx({
      tvShowId,
      seasonId: id,
    })
    return season?.purchaseInfo != null
  }
)

const isTvShowEpisodePurchasedFx = createEffect(
  async (params: { id: number; seasonId?: number; tvShowId: number }) => {
    let episode: TvShowEpisode

    if (params.seasonId) {
      episode = await api.tvshows.getTvShowEpisodeFx({
        tvShowId: params.tvShowId,
        seasonId: params.seasonId,
        episodeId: params.id,
      })
    } else {
      episode = await api.tvshows.getTvShowEpisodeWithoutSeasonFx({
        tvShowId: params.tvShowId,
        episodeId: params.id,
      })
    }

    return episode?.purchaseInfo != null
  }
)

const isTvShowBundlePurchasedFx = createEffect(
  async ({ id }: { id: number }) => {
    const bundle = await api.showBundles.getShowBundleInfoFx({ bundleId: id })
    return bundle?.purchaseInfo != null
  }
)

export const isPurchasedFx = createEffect(
  async ({
    id,
    seasonId,
    tvShowId,
    mediaType,
  }: ExternalPaymentParams): Promise<boolean> => {
    try {
      switch (mediaType) {
        case MediaTypes.TV_CHANNEL:
        case MediaTypesExtended.TV_CHANNEL_IN_BUNDLE:
        case MediaTypesExtended.TV_CHANNEL_CATCHUP:
          return await isTvChannelPurchasedFx({ id })
        case MediaTypesExtended.TV_CHANNEL_BUNDLE:
          return await isTvChannelBundlePurchasedFx({ id })
        case MediaTypes.VOD:
        case MediaTypesExtended.VOD_IN_BUNDLE:
          return await isVodPurchasedFx({ id })
        case MediaTypesExtended.VOD_BUNDLE:
          return await isVodBundlePurchasedFx({ id })
        case MediaTypes.TV_SHOW:
        case MediaTypesExtended.TV_SHOW_IN_BUNDLE:
          return await isTvShowPurchasedFx({ id })
        case MediaTypesExtended.TV_SHOW_SEASON:
        case MediaTypesExtended.TV_SHOW_SEASON_IN_BUNDLE:
          return (
            tvShowId != null &&
            (await isTvShowSeasonPurchasedFx({ id, tvShowId }))
          )
        case MediaTypesExtended.TV_SHOW_EPISODE:
        case MediaTypesExtended.TV_SHOW_EPISODE_IN_BUNDLE:
          return (
            tvShowId != null &&
            (await isTvShowEpisodePurchasedFx({
              id,
              seasonId: seasonId,
              tvShowId: tvShowId,
            }))
          )
        case MediaTypesExtended.TV_SHOW_BUNDLE:
          return await isTvShowBundlePurchasedFx({ id })
        case MediaTypes.PARTNER_PRODUCT:
          return await isPartnerProductPurchasedFx({ id })
        case MediaTypes.LIVE_EVENT:
          return await isLiveEventPurchasedFx({ id })
      }
    } catch (e) {
      // do nothing
    }
    return false
  }
)

const externalIsPurchasedFx = attach({ effect: isPurchasedFx })

sample({
  clock: selectForExternalPayment,
  target: externalIsPurchasedFx,
})

sample({
  clock: externalIsPurchasedFx.done,
  filter: ({ result: isPurchased }) => isPurchased,
  fn: ({ params }) => ({
    mediaType: params.shouldRefresh
      ? MediaTypeInternal.__REFRESH__
      : params.mediaType,
    params: params.params,
  }),
  target: redirectToPlayerOrRefresh,
})

split({
  source: redirectToPlayerOrRefresh,
  match: ({ mediaType }) => mediaType as unknown, // mark as `unknown` due to poor typescript support in `split`
  cases: {
    TV_CHANNEL: redirectUrlModel.redirectToTvPlayer,
    TV_SHOW_EPISODE: redirectUrlModel.redirectToShowPlayer,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TV_CHANNEL_CATCHUP: catchupsModel.navigateCatchupPlayer.prepend<any>(
      ({ params }) => params
    ),
    __REFRESH__: router.refresh,
  },
})

sample({
  clock: externalIsPurchasedFx.done,
  filter: ({ result: isPurchased }) => !isPurchased,
  target: openExternalPaymentPopup,
})
